import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Play, Pause, RotateCcw, CheckCircle2, 
  Plus, Trash2, Clock, Coffee, Brain, 
  BarChart2, Calendar, Zap, Bed
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { POMODORO_SETTINGS, getTimerState, saveTimerState } from '@/lib/productivity-utils';
import { cn } from '@/lib/utils';
import { productivityAPI } from '@/services/productivity';

const DailyFocusPage = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  // ✅ Workflow Mode (focus vs rest)
  const [workflowMode, setWorkflowMode] = useState('focus');
  
  // Timer State
  const [timeLeft, setTimeLeft] = useState(POMODORO_SETTINGS.FOCUS_TIME);
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState('focus');
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  // Data State
  const [tasks, setTasks] = useState([]);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [focusTimeToday, setFocusTimeToday] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // ✅ Assignment State
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(user.id);

  // Refs
  const timerRef = useRef(null);
  const hasGreeted = useRef(false);

  // ✅ Timer Complete - Save to API
  const handleTimerComplete = useCallback(async () => {
    clearInterval(timerRef.current);
    setIsActive(false);
    
    try {
      const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-software-interface-start-2574.mp3');
      audio.play().catch(() => {});
    } catch (e) {}

    // ✅ Focus Mode: Pomodoro cycle
    if (workflowMode === 'focus') {
      if (mode === 'focus') {
        // Save Full Session to API
        try {
          const durationInSeconds = POMODORO_SETTINGS.FOCUS_TIME;
          await productivityAPI.createSession({
            userId: user.id,
            taskId: activeTaskId,
            duration: durationInSeconds,
            type: 'focus'
          });

          const minutes = Math.floor(durationInSeconds / 60);
          setFocusTimeToday(prev => prev + minutes);
          addNotification("Session Complete!", "Great job! 25 minutes completed.", "success");
        } catch (error) {
          console.error('Failed to save session:', error);
        }

        setSessionStartTime(null);
        setSessionsCompleted(prev => {
          const newCompleted = prev + 1;
          
          if (newCompleted % POMODORO_SETTINGS.SESSIONS_BEFORE_LONG_BREAK === 0) {
            setMode('long-break');
            setTimeLeft(POMODORO_SETTINGS.LONG_BREAK);
            addNotification("Long Break!", "Great job! Take a solid rest now.", "success");
          } else {
            setMode('short-break');
            setTimeLeft(POMODORO_SETTINGS.SHORT_BREAK);
            addNotification("Short Break", "Take a breather.", "success");
          }
          
          return newCompleted;
        });
      } else {
        setMode('focus');
        setTimeLeft(POMODORO_SETTINGS.FOCUS_TIME);
        addNotification("Back to Work", "Let's focus again!", "info");
      }
    } 
    // ✅ Rest Mode: Just notify and reset
    else {
      addNotification("Rest Complete", "Hope you feel refreshed!", "success");
      setTimeLeft(POMODORO_SETTINGS.SHORT_BREAK);
      setSessionStartTime(null);
    }
  }, [workflowMode, mode, user.id, activeTaskId, addNotification]);

  // ✅ Load Tasks and Users from API
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load users if Admin/Manager
        if (user.role === 'Admin' || user.role === 'Manager') {
          try {
            const usersResponse = await productivityAPI.getUsers();
            if (usersResponse.success) {
              setUsers(usersResponse.data || []);
            }
          } catch (error) {
            console.error('Failed to load users:', error);
          }
        }
        
        // Load tasks
        const response = await productivityAPI.getTasks(user.id);
        if (response.success) {
          const today = new Date().toISOString().split('T')[0];
          const relevantTasks = response.data.filter(t => {
            if (!t.completed_at) return true;
            return t.completed_at.startsWith(today);
          });
          setTasks(relevantTasks);
        }
      } catch (error) {
        console.error('Failed to load tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Load Timer State from localStorage
    const savedState = getTimerState();
    if (savedState && savedState.userId === user.id) {
      const now = Date.now();
      
      if (savedState.isActive) {
        const elapsed = Math.floor((now - savedState.lastTick) / 1000);
        const newTimeLeft = Math.max(0, savedState.timeLeft - elapsed);
        
        setTimeLeft(newTimeLeft);
        setIsActive(newTimeLeft > 0);
        setMode(savedState.mode);
        setWorkflowMode(savedState.workflowMode || 'focus');
        setActiveTaskId(savedState.activeTaskId);
        setSessionsCompleted(savedState.sessionsCompleted || 0);
        if (savedState.sessionStartTime) {
          setSessionStartTime(savedState.sessionStartTime);
        }
      } else {
        setTimeLeft(savedState.timeLeft);
        setMode(savedState.mode);
        setWorkflowMode(savedState.workflowMode || 'focus');
        setActiveTaskId(savedState.activeTaskId);
        setSessionsCompleted(savedState.sessionsCompleted || 0);
      }
    }

    // Greeting
    const hour = new Date().getHours();
    if (hour < 10 && !hasGreeted.current) {
      addNotification("Good Morning", "Ready to start a productive day?", "info");
      hasGreeted.current = true;
    }
  }, [user.id, user.role, addNotification]);

  // ✅ Load Focus Time from API
  useEffect(() => {
    const loadFocusTime = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await productivityAPI.getSessions(user.id, { date: today });
        if (response.success && response.data) {
          const totalSeconds = response.data.reduce((sum, s) => sum + (s.duration || 0), 0);
          setFocusTimeToday(Math.floor(totalSeconds / 60));
        }
      } catch (error) {
        console.error('Failed to load focus time:', error);
      }
    };

    loadFocusTime();
    const interval = setInterval(loadFocusTime, 60000);
    return () => clearInterval(interval);
  }, [user.id]);

  // Persist Timer State
  useEffect(() => {
    const state = {
      userId: user.id,
      timeLeft,
      isActive,
      mode,
      workflowMode,
      activeTaskId,
      sessionsCompleted,
      sessionStartTime,
      lastTick: Date.now()
    };
    saveTimerState(state);
  }, [timeLeft, isActive, mode, workflowMode, activeTaskId, sessionsCompleted, sessionStartTime, user.id]);

  // Timer Tick
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [isActive, timeLeft, handleTimerComplete]);

  // ✅ Save Partial Session on Pause
  const savePartialSession = async (timeSpent) => {
    if (workflowMode !== 'focus' || mode !== 'focus' || timeSpent < 60) return;

    try {
      await productivityAPI.createSession({
        userId: user.id,
        taskId: activeTaskId,
        duration: timeSpent,
        type: 'focus'
      });

      const minutes = Math.floor(timeSpent / 60);
      setFocusTimeToday(prev => prev + minutes);
      addNotification("Session Saved!", `Recorded ${minutes} minutes of focus time.`, "success");
    } catch (error) {
      console.error('Failed to save partial session:', error);
    }
  };

  // ✅ Toggle Timer
  const toggleTimer = async () => {
    if (isActive) {
      // Pausing
      if (sessionStartTime && workflowMode === 'focus' && mode === 'focus') {
        const timeSpent = Math.floor((Date.now() - sessionStartTime) / 1000);
        if (timeSpent >= 60) {
          await savePartialSession(timeSpent);
        }
        setSessionStartTime(null);
      }
      setIsActive(false);
    } else {
      // Starting
      if (workflowMode === 'focus' && mode === 'focus') {
        setSessionStartTime(Date.now());
      }
      setIsActive(true);
    }
  };

  // ✅ Reset Timer
  const resetTimer = async () => {
    if (isActive && sessionStartTime && workflowMode === 'focus' && mode === 'focus') {
      const timeSpent = Math.floor((Date.now() - sessionStartTime) / 1000);
      await savePartialSession(timeSpent);
    }

    setIsActive(false);
    setSessionStartTime(null);
    
    if (workflowMode === 'focus') {
      setTimeLeft(
        mode === 'focus' ? POMODORO_SETTINGS.FOCUS_TIME :
        mode === 'short-break' ? POMODORO_SETTINGS.SHORT_BREAK : POMODORO_SETTINGS.LONG_BREAK
      );
    } else {
      setTimeLeft(POMODORO_SETTINGS.SHORT_BREAK);
    }
  };

  // ✅ Switch Workflow Mode
  const switchWorkflowMode = (newMode) => {
    if (isActive) {
      addNotification("Timer Active", "Please pause the timer first", "warning");
      return;
    }
    
    setWorkflowMode(newMode);
    
    if (newMode === 'focus') {
      setMode('focus');
      setTimeLeft(POMODORO_SETTINGS.FOCUS_TIME);
    } else {
      setMode('rest');
      setTimeLeft(POMODORO_SETTINGS.SHORT_BREAK);
    }
    
    setSessionStartTime(null);
  };

  // ✅ Add Task to API with Assignment
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    try {
      const response = await productivityAPI.createTask({
        userId: user.id, // creator
        assignedTo: assignedTo, // assigned to
        assignedBy: assignedTo !== user.id ? user.id : null, // if assigning to someone else
        title: newTaskTitle,
        status: 'todo',
        priority: 'medium'
      });

      setTasks([response.data, ...tasks]);
      setNewTaskTitle('');
      setAssignedTo(user.id); // reset to self
      
      if (assignedTo !== user.id) {
        const assignedUser = users.find(u => u.id === assignedTo);
        addNotification('Task Assigned', `Task assigned to ${assignedUser?.name}`, 'success');
      }
    } catch (error) {
      console.error('Failed to add task:', error);
      addNotification('Error', 'Failed to add task', 'error');
    }
  };

  // ✅ Toggle Task Status
  const toggleTaskStatus = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';

      const response = await productivityAPI.updateTask(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? response.data : t));
    } catch (error) {
      console.error('Failed to update task:', error);
      addNotification('Error', 'Failed to update task', 'error');
    }
  };

  // ✅ Delete Task
  const deleteTask = async (taskId) => {
    try {
      await productivityAPI.deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
      if (activeTaskId === taskId) setActiveTaskId(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
      addNotification('Error', 'Failed to delete task', 'error');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (workflowMode === 'rest') {
      return ((POMODORO_SETTINGS.SHORT_BREAK - timeLeft) / POMODORO_SETTINGS.SHORT_BREAK) * 100;
    }
    
    const total = mode === 'focus' ? POMODORO_SETTINGS.FOCUS_TIME : 
                 mode === 'short-break' ? POMODORO_SETTINGS.SHORT_BREAK : POMODORO_SETTINGS.LONG_BREAK;
    return ((total - timeLeft) / total) * 100;
  };

  // Metrics
  const todayTasks = tasks.filter(t => {
    const taskDate = t.created_at ? t.created_at.split(' ')[0] : t.date;
    const today = new Date().toISOString().split('T')[0];
    return taskDate === today;
  });
  const completedToday = todayTasks.filter(t => t.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Helmet><title>Daily Focus - Productivity</title></Helmet>
      
      {/* Top Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-500 uppercase font-bold">Today's Tasks</p>
              <h3 className="text-2xl font-bold text-navy-steal">{completedToday} / {todayTasks.length}</h3>
            </div>
            <div className="bg-blue-50 p-2 rounded-full"><CheckCircle2 className="w-5 h-5 text-blue-500" /></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-purple-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-500 uppercase font-bold">Focus Time</p>
              <h3 className="text-2xl font-bold text-navy-steal">{focusTimeToday} <span className="text-sm font-normal text-stone-400">min</span></h3>
            </div>
            <div className="bg-purple-50 p-2 rounded-full"><Brain className="w-5 h-5 text-purple-500" /></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-orange-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-500 uppercase font-bold">Pomodoros</p>
              <h3 className="text-2xl font-bold text-navy-steal">{sessionsCompleted}</h3>
            </div>
            <div className="bg-orange-50 p-2 rounded-full"><Clock className="w-5 h-5 text-orange-500" /></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-green-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-500 uppercase font-bold">Efficiency</p>
              <h3 className="text-2xl font-bold text-navy-steal">{todayTasks.length ? Math.round((completedToday/todayTasks.length)*100) : 0}%</h3>
            </div>
            <div className="bg-green-50 p-2 rounded-full"><BarChart2 className="w-5 h-5 text-green-500" /></div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Timer Section */}
        <div className="lg:col-span-1">
          <Card className={cn(
            "h-full shadow-md border-t-4 transition-colors duration-500",
            workflowMode === 'focus' 
              ? (mode === 'focus' ? "border-t-safety-orange" : "border-t-green-500")
              : "border-t-blue-500"
          )}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>
                  {workflowMode === 'focus' 
                    ? (mode === 'focus' ? 'Focus Timer' : 'Break Time')
                    : 'Rest Timer'}
                </span>
                <Badge variant="outline" className={cn(
                  workflowMode === 'focus'
                    ? (mode === 'focus' ? "bg-orange-50 text-orange-600 border-orange-200" : "bg-green-50 text-green-600 border-green-200")
                    : "bg-blue-50 text-blue-600 border-blue-200"
                )}>
                  {workflowMode === 'focus' 
                    ? (mode === 'focus' ? <><Brain className="w-3 h-3 mr-1" /> FOCUS</> : <><Coffee className="w-3 h-3 mr-1" /> BREAK</>)
                    : <><Bed className="w-3 h-3 mr-1" /> REST</>
                  }
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-6 space-y-6">
              {/* ✅ Workflow Mode Selector */}
              <div className="flex gap-2 w-full max-w-xs">
                <Button
                  variant={workflowMode === 'focus' ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    "flex-1 h-9",
                    workflowMode === 'focus' && "bg-safety-orange hover:bg-orange-600"
                  )}
                  onClick={() => switchWorkflowMode('focus')}
                >
                  <Zap className="w-4 h-4 mr-1.5" />
                  Focus Mode
                </Button>
                <Button
                  variant={workflowMode === 'rest' ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    "flex-1 h-9",
                    workflowMode === 'rest' && "bg-blue-500 hover:bg-blue-600"
                  )}
                  onClick={() => switchWorkflowMode('rest')}
                >
                  <Bed className="w-4 h-4 mr-1.5" />
                  Rest Mode
                </Button>
              </div>

              {/* Circular Progress Timer */}
              <div className="relative w-64 h-64 flex items-center justify-center">
                 <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="128" cy="128" r="120"
                      className="stroke-stone-100 fill-none"
                      strokeWidth="8"
                    />
                    <circle
                      cx="128" cy="128" r="120"
                      className={cn(
                        "fill-none transition-all duration-1000 ease-linear",
                        workflowMode === 'focus'
                          ? (mode === 'focus' ? "stroke-safety-orange" : "stroke-green-500")
                          : "stroke-blue-500"
                      )}
                      strokeWidth="8"
                      strokeDasharray={2 * Math.PI * 120}
                      strokeDashoffset={((100 - getProgress()) / 100) * (2 * Math.PI * 120)}
                      strokeLinecap="round"
                    />
                 </svg>
                 <div className="absolute flex flex-col items-center">
                    <span className="text-5xl font-bold text-navy-steal tabular-nums tracking-tight">
                      {formatTime(timeLeft)}
                    </span>
                    <span className="text-sm text-stone-400 mt-2 font-medium">
                      {isActive ? 'RUNNING' : 'PAUSED'}
                    </span>
                 </div>
              </div>

              <div className="flex items-center gap-4 w-full justify-center">
                 <Button 
                    size="lg" 
                    className={cn(
                      "w-32 h-12 text-lg font-bold shadow-lg transition-transform active:scale-95",
                      isActive 
                        ? "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300" 
                        : (workflowMode === 'focus' 
                            ? (mode === 'focus' ? "bg-safety-orange hover:bg-orange-600 text-white" : "bg-green-500 hover:bg-green-600 text-white")
                            : "bg-blue-500 hover:bg-blue-600 text-white")
                    )}
                    onClick={toggleTimer}
                 >
                    {isActive ? <><Pause className="mr-2" /> Pause</> : <><Play className="mr-2" /> Start</>}
                 </Button>
                 <Button 
                    size="icon" 
                    variant="outline" 
                    className="h-12 w-12 rounded-full border-2"
                    onClick={resetTimer}
                 >
                    <RotateCcw className="w-5 h-5 text-stone-500" />
                 </Button>
              </div>

              {activeTaskId && workflowMode === 'focus' && (
                <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 max-w-full truncate">
                   <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                   Working on: {tasks.find(t => t.id === activeTaskId)?.title || 'Task'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Task List Section */}
        <div className="lg:col-span-2 flex flex-col gap-6">
           <Card className="flex-1 shadow-sm border-stone-200">
             <CardHeader className="bg-slate-50 border-b border-stone-100">
                <CardTitle className="text-lg">Tasks for Today</CardTitle>
             </CardHeader>
             <CardContent className="p-0">
                <div className="p-4 border-b border-stone-100">
                  <form onSubmit={handleAddTask} className="space-y-3">
                    {/* ✅ User Selector (Admin/Manager only) */}
                    {(user.role === 'Admin' || user.role === 'Manager') && users.length > 0 && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-stone-600 min-w-[90px]">
                          Assign to:
                        </label>
                        <select
                          value={assignedTo}
                          onChange={(e) => setAssignedTo(e.target.value)}
                          className="flex-1 px-3 py-2 border border-stone-200 rounded-md text-sm focus:border-safety-orange focus:ring-safety-orange/20 focus:outline-none"
                        >
                          <option value={user.id}>Myself</option>
                          {users.filter(u => u.id !== user.id).map(u => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Task Input */}
                    <div className="flex gap-2">
                      <Input 
                        placeholder="What needs to be done?" 
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        className="flex-1 border-stone-200 focus:border-safety-orange focus:ring-safety-orange/20"
                      />
                      <Button type="submit" className="bg-navy-steal hover:bg-charcoal text-white">
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                  </form>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto">
                   {tasks.length === 0 ? (
                     <div className="flex flex-col items-center justify-center py-12 text-stone-400">
                        <Calendar className="w-12 h-12 mb-2 opacity-20" />
                        <p>No tasks yet. Add one above!</p>
                     </div>
                   ) : (
                     <ul className="divide-y divide-stone-100">
                       {tasks.map(task => (
                         <li 
                            key={task.id} 
                            className={cn(
                              "flex flex-col p-4 hover:bg-slate-50 transition-colors group",
                              activeTaskId === task.id && "bg-blue-50/50 hover:bg-blue-50/70"
                            )}
                         >
                           <div className="flex items-center justify-between">
                             <div className="flex items-center gap-3 flex-1 min-w-0">
                               <button 
                                 onClick={() => toggleTaskStatus(task.id)}
                                 className={cn(
                                   "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                                   task.status === 'completed' 
                                     ? "bg-green-500 border-green-500 text-white" 
                                     : "border-stone-300 text-transparent hover:border-safety-orange"
                                 )}
                               >
                                 <CheckCircle2 className="w-3.5 h-3.5" />
                               </button>
                               
                               <div className="flex-1 min-w-0">
                                 <span className={cn(
                                   "block truncate text-sm font-medium",
                                   task.status === 'completed' ? "text-stone-400 line-through" : "text-navy-steal"
                                 )}>
                                   {task.title}
                                 </span>
                                 
                                 {/* ✅ Show assignment info */}
                                 <div className="flex flex-wrap gap-2 mt-1">
                                   {task.assigned_to_name && task.assigned_to !== task.user_id && (
                                     <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                       Assigned to: {task.assigned_to_name}
                                     </span>
                                   )}
                                   {task.assigned_by_name && task.assigned_by !== task.user_id && (
                                     <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                       By: {task.assigned_by_name}
                                     </span>
                                   )}
                                 </div>
                               </div>
                             </div>

                             <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                               {task.status !== 'completed' && workflowMode === 'focus' && (
                                 <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className={cn(
                                      "h-8 text-xs font-medium",
                                      activeTaskId === task.id ? "text-blue-600 bg-blue-100 hover:bg-blue-200" : "text-stone-500 hover:bg-stone-100"
                                    )}
                                    onClick={() => setActiveTaskId(task.id === activeTaskId ? null : task.id)}
                                 >
                                    {activeTaskId === task.id ? "Active" : "Focus"}
                                 </Button>
                               )}
                               <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => deleteTask(task.id)}
                               >
                                  <Trash2 className="w-4 h-4" />
                               </Button>
                             </div>
                           </div>
                         </li>
                       ))}
                     </ul>
                   )}
                </div>
             </CardContent>
           </Card>
        </div>
      </div>
    </div>
  );
};

export default DailyFocusPage;
